import React, { useEffect, useState } from 'react';
import { Box, Button, Divider, Grid, Paper, Typography, useTheme } from '@mui/material';
import TopBar from '../../../components/TopBar';
import { useForm, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import CountrySelect from '../../../components/form/CountrySelect';
import UAESubdivisionSelect from '../../../components/form/UAESubdivisionSelect';
import RegistrationTypeSelect from '../../../components/form/RegistrationTypeSelect';
import PartyIdentificationSchemeSelect from '../../../components/form/PartyIdentificationSchemeSelect';
import { BusinessProfileValidationSchema } from '../../../validations/BusinessProfileValidation';
import { isEmpty, map } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import CustomTextInput from '../../../components/CustomTextInput';
import PeppolRegistrationSteps from '../../../components/form/PeppolRegistrationsSteps';
import { fetchParticipantRequest, resetParticipant } from '../../../api/participants/participantsSlice';
import PeppolDetails from '../../../components/form/PeppolDetails';
import { showSnackbar } from '../../../api/snackbarSlice';
import LogoUpload from '../../../components/LogoUpload';
import { uploadLogoToCloud } from '../../../helper/UploadLogoToCloud';
import { useTranslatedMessages } from '../../../hooks/useTranslatedMessages';
import { useRevalidateFormOnLanguageChange } from '../../../hooks/useRevalidateFormOnLanguageChange';
import TaxSchemeSelect from '../../../components/form/TaxSchemeSelect';
import {
  getCountryOptionByCode,
  getRegistrationTypeOptionByCode,
  getSubdivisionOptionByCode,
  getCountryOptions,
  getSubdivisionOptions,
  getRegistrationTypeOptions,
  getB2BRegistrationTypeOptionsExcludingOTH,
  getIso6523IcdOptionByCode,
} from '../../../api/codelist/codeListsSlice';
import { UAE_COUNTRY_CODE } from '../../../constants/defaults';
import { mapEmaraTaxResponseToFormValues } from '../dataHandlers/emaraTaxResponseMapper';
import { buildBusinessProfileUpdatePayload } from '../dataHandlers/buildBusinessProfileUpdatePayload';
// import useScrollToError from '../../../hooks/useScrollToError';

export default function BusinessProfileForm({
  onSubmit,
  onCancel,
  initialData = {},
  apiErrors = {},
  edit = false,
  readOnly = false,
  isVerified = true,
  isLoading = false,
  verifiedBusinessProfile = null,
}) {
  const theme = useTheme();
  const [file, setFile] = useState(null);
  const dispatch = useDispatch();
  const { snackbarMessages, businessProfiles, validations, language } = useTranslatedMessages();
  const settingsMessages = snackbarMessages.settings ?? {};
  const actions = businessProfiles?.actions ?? {};
  const formTexts = businessProfiles?.form ?? {};
  const labels = formTexts?.labels ?? {};
  const sections = formTexts?.sections ?? {};
  const formPage = businessProfiles?.pages?.form ?? {};

  const countryOption = useSelector(getCountryOptionByCode(initialData.postal_address?.country_code));
  const uaeCountryOption = useSelector(getCountryOptionByCode(UAE_COUNTRY_CODE));
  const subdivisionOption = useSelector(getSubdivisionOptionByCode(initialData.postal_address?.country_subentity));
  const schemeAgencyIdOption = useSelector(getRegistrationTypeOptionByCode(initialData.scheme_agency_id));
  const passportIssuingCountryOption = useSelector(getCountryOptionByCode(initialData.passport_issuing_country_code));
  const partyIdentificationSchemeOption = useSelector(
    getIso6523IcdOptionByCode(initialData.party_identification_scheme_id),
  );
  const subscription = useSelector((state) => state.subscription.subscription);

  // Get options lists for mapping API response strings to objects
  const countryOptions = useSelector(getCountryOptions);
  const subdivisionOptions = useSelector(getSubdivisionOptions);
  const registrationTypeOptions = useSelector(getRegistrationTypeOptions);

  const participant = useSelector((state) => state.participants.participant);

  const [participantId, setParticipantId] = useState(initialData.participant_id || '');
  const [peppolParticipant, setPeppolParticipant] = useState(initialData.peppol_participant || {});
  const [loading, setLoading] = useState(false);
  const [loadingParticipant, setLoadingParticipant] = useState(false);
  const [autoPopulatedFields, setAutoPopulatedFields] = useState(new Set());
  const b2bRegistrationTypeOptions = useSelector(getB2BRegistrationTypeOptionsExcludingOTH);
  const hasExistingVat = Boolean(initialData?.party_tax_scheme?.company_id?.trim());
  const resolvedCountryOption =
    countryOption?.code === UAE_COUNTRY_CODE ? countryOption : (uaeCountryOption ?? countryOption);

  const handleRegisterType = () => {
    setValue('company_id', '');
    setValue('authority_name', '');
    setValue('passport_issuing_country_code', null);
  };

  const {
    register,
    control,
    getValues,
    setValue,
    watch,
    handleSubmit,
    setError,
    formState: { errors },
    trigger,
  } = useForm({
    resolver: yupResolver(BusinessProfileValidationSchema(validations, { isEdit: edit })),
    defaultValues: {
      ...initialData,
      party_tax_scheme: {
        tax_scheme: initialData.party_tax_scheme?.tax_scheme ?? '',
        company_id: initialData.party_tax_scheme?.company_id ?? '',
      },
      postal_address: {
        ...initialData.postal_address,
        street_name: initialData.postal_address?.street_name,
        additional_street_name: initialData.postal_address?.additional_street_name,
        address_line: initialData.postal_address?.address_line,
        city_name: initialData.postal_address?.city_name,
        postal_zone: initialData.postal_address?.postal_zone,
        country_code: resolvedCountryOption,
        country_subentity: subdivisionOption ?? null,
      },
      telephone: initialData.telephone,
      scheme_agency_id: schemeAgencyIdOption,
      passport_issuing_country_code: passportIssuingCountryOption,
      party_identification_id: initialData.party_identification_id ?? '',
      party_identification_scheme_id: partyIdentificationSchemeOption ?? null,
      peppol_participant: initialData.peppol_participant ? initialData.peppol_participant : {},
      endpointSchemeId: initialData?.endpoint_scheme_id || '',
      participant_id:
        initialData?.endpoint_id && initialData?.endpoint_scheme_id
          ? `${initialData?.endpoint_scheme_id}:${initialData?.endpoint_id}`
          : '',
    },
  });

  useRevalidateFormOnLanguageChange(language, trigger, errors);

  useEffect(() => {
    map(apiErrors, (value, key) => {
      setError(key, { message: value, type: 'string' });
    });
  }, [setError, apiErrors]);

  // useScrollToError(errors, apiErrors);

  // useEffect(() => {
  //   const currentParticipantId = getValues('participant_id');
  //   setParticipantId(currentParticipantId);
  //   if (currentParticipantId) {
  //     handleFetchParticipants();
  //   }

  //   return () => {
  //     dispatch(resetParticipant());
  //   };
  // }, [participantId]);

  useEffect(() => {
    if (participantId) {
      setValue('participant_id', participantId);
    }
  }, [participantId]);

  useEffect(() => {
    if (peppolParticipant?.identifier_code && peppolParticipant?.special_identifier && peppolParticipant?.identifier) {
      const newParticipantId = `${peppolParticipant.identifier_code}:${peppolParticipant.special_identifier}${peppolParticipant.identifier}`;
      setParticipantId(newParticipantId);
    }
  }, [peppolParticipant]);

  // Auto-populate all form fields when verified business profile data is available
  useEffect(() => {
    if (isVerified && verifiedBusinessProfile) {
      // Use mapper to convert API response to form values
      const formValues = mapEmaraTaxResponseToFormValues(verifiedBusinessProfile, {
        registrationTypeOptions,
      });

      // Track which fields are auto-populated
      const populatedFields = new Set(Object.keys(formValues));
      setAutoPopulatedFields(populatedFields);

      // Set all mapped form values
      Object.entries(formValues).forEach(([fieldPath, value]) => {
        setValue(fieldPath, value);
      });
    }
  }, [isVerified, verifiedBusinessProfile, setValue, registrationTypeOptions]);

  // Helper function to check if a field should be disabled
  const isFieldDisabled = (fieldName) => {
    return isFormDisabled || autoPopulatedFields.has(fieldName);
  };

  const handleFetchParticipants = async () => {
    if (!participantId) return;
    if (!subscription?.features?.enable_phase2) {
      setLoading(false);
      setLoadingParticipant(false);
      return;
    }

    setLoading(true);
    setLoadingParticipant(true);

    const [identifier_code, rest] = participantId.split(':');
    const match = rest ? rest.match(/(\d{2})(.*)/) : [];
    const [special_identifier, identifier] = match ? match.slice(1) : [];

    try {
      if (subscription?.features?.enable_phase2)
        await dispatch(fetchParticipantRequest({ identifier_code, identifier, special_identifier }));
    } catch (error) {
      console.error('Error fetching participant:', error);
    } finally {
      setLoading(false);
    }
  };

  const isPeppolEmpty = !peppolParticipant?.endpoint_id;

  useEffect(() => {
    setLoadingParticipant(false);
    if (!isEmpty(participant)) {
      const match = participant.idValue?.match(/(\d{2})(.*)/);
      const [special_identifier, identifier] = match ? match.slice(1) : [];

      setPeppolParticipant({
        scheme: participant?.idScheme,
        identifier: identifier,
        identifier_code: participant?.idType,
        special_identifier: special_identifier,
        endpoint_id: participant?.endpoint[0]?.url,
      });
    }
    if (participantId === '') {
      setPeppolParticipant({});
    }
  }, [participant]);

  const handleFormSubmit = async (data) => {
    setLoading(true);
    let logoUrl = initialData?.logo_url ?? '';

    if (file) {
      logoUrl = await uploadLogoToCloud(file);

      if (!logoUrl) {
        dispatch(showSnackbar({ message: settingsMessages.organization_settings_updated_success, severity: 'error' }));
        setLoading(false);
        return;
      }
    }
    setLoading(false);

    if (edit) {
      onSubmit(
        buildBusinessProfileUpdatePayload(data, {
          profileId: initialData.profile_id,
          logoUrl,
        }),
      );

      return;
    }

    onSubmit({
      ...data,
      participant_id: participantId,
      scheme_agency_id: data.scheme_agency_id?.code,
      logo_url: logoUrl,
      tin: data.tin?.trim() || data.tin,
      postal_address: {
        ...data.postal_address,
        country_code: UAE_COUNTRY_CODE,
        country: data.postal_address?.country_code?.label,
        country_subentity: data.postal_address?.country_subentity?.code,
      },
    });
  };

  const registrationType = useWatch({ control, name: 'scheme_agency_id' });

  const registrationNumberLabel = registrationType
    ? `${registrationType.label} (${registrationType?.code})`
    : (labels.registration_number ?? 'Registration Number');

  // Determine if form should be disabled (when not verified and not in edit mode)
  const isFormDisabled = !isVerified && !edit;

  return (
    <Paper elevation={0} sx={{ p: '1rem', mt: '-1rem', opacity: isFormDisabled ? 0.6 : 1 }}>
      {!readOnly && (
        <Box sx={{ pt: 2, pb: 4 }}>
          <Grid
            container
            sx={{
              display: 'block',
            }}
          >
            <Grid>
              <TopBar title={formPage?.top_bar_title ?? 'Business Profile Form'} identifier={initialData?.profile_id}>
                <Box>
                  <Button
                    data-testid="cancel-button"
                    key={'cancel'}
                    variant="outlined"
                    onClick={onCancel}
                    disabled={isFormDisabled || isLoading}
                    sx={{ marginLeft: theme.spacing(1) }}
                  >
                    {actions.cancel ?? 'Cancel'}
                  </Button>
                  <Button
                    data-testid="business-profile-save"
                    key={'save'}
                    variant="contained"
                    onClick={handleSubmit(handleFormSubmit)}
                    disabled={isFormDisabled || isLoading}
                    sx={{ marginLeft: theme.spacing(1) }}
                  >
                    {actions.submit ?? 'Submit'}
                  </Button>
                </Box>
              </TopBar>
            </Grid>
          </Grid>
        </Box>
      )}
      <Box>
        <Grid container spacing={2}>
          <Grid container spacing={2} size={{ xs: 4, sm: 8 }}>
            <Grid size={{ xs: 12, sm: 12 }}>
              <CustomTextInput
                data-testid="name-input"
                name="name"
                control={control}
                errors={errors}
                readOnly={readOnly || isFieldDisabled('name')}
                label={labels.name ?? 'Name'}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 12 }}>
              <CustomTextInput
                data-testid="business-name-input"
                name="party_name"
                control={control}
                errors={errors}
                readOnly={readOnly || isFieldDisabled('party_name') || edit}
                label={labels.business_name ?? 'Business Name'}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 12 }}>
              <CustomTextInput
                data-testid="business-name-in-local-language-input"
                name="party_name_in_local_language"
                control={control}
                errors={errors}
                readOnly={readOnly || isFieldDisabled('party_name_in_local_language') || edit}
                label={labels.business_name_local ?? 'Business Name in Local Language'}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 12 }}>
              <CustomTextInput
                data-testid="email-input"
                name="email"
                control={control}
                errors={errors}
                readOnly={readOnly || isFieldDisabled('email') || edit}
                label={labels.email ?? 'Email'}
              />
            </Grid>
          </Grid>
          <Grid size={{ xs: 4, sm: 4 }}>
            <LogoUpload
              initialLogoUrl={initialData?.logo_url}
              onFileChange={setFile}
              readOnly={readOnly || isFormDisabled}
            />
          </Grid>
        </Grid>

        <Typography variant="h6" sx={{ mb: '1.5rem', pt: '1rem' }}>
          {sections.address ?? 'Address'}
          <Divider></Divider>
        </Typography>

        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <CustomTextInput
              data-testid="street_name-input"
              name="postal_address.street_name"
              control={control}
              errors={errors}
              readOnly={readOnly}
              disabled={isFormDisabled}
              label={labels.street_name ?? 'Street Name'}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <CustomTextInput
              data-testid="additional_street_name-input"
              name="postal_address.additional_street_name"
              control={control}
              errors={errors}
              readOnly={readOnly}
              disabled={isFormDisabled}
              label={labels.additional_street_name ?? 'Additional Street Name'}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <CustomTextInput
              data-testid="city_name-input"
              name="postal_address.city_name"
              control={control}
              errors={errors}
              readOnly={readOnly}
              disabled={isFormDisabled}
              label={labels.city_name ?? 'City Name'}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <CustomTextInput
              data-testid="address_line-input"
              name="postal_address.address_line"
              control={control}
              errors={errors}
              readOnly={readOnly}
              disabled={isFormDisabled}
              label={labels.address_line ?? 'Address Line'}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <CountrySelect
              data-testid="country_code-select"
              name="postal_address.country_code"
              control={control}
              errors={errors}
              readOnly={readOnly}
              disabled={isFormDisabled}
              allowedCodes={[UAE_COUNTRY_CODE]}
              label={labels.country ?? 'Country'}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <UAESubdivisionSelect
              data-testid="country_subentity-select"
              errors={errors}
              control={control}
              name="postal_address.country_subentity"
              readOnly={readOnly}
              disabled={isFormDisabled}
              label={labels.uae_subdivision ?? 'UAE Subdivision'}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <CustomTextInput
              data-testid="postal_zone-input"
              name="postal_address.postal_zone"
              control={control}
              errors={errors}
              readOnly={readOnly}
              disabled={isFormDisabled}
              label={labels.postal_zone ?? 'Postal Zone'}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <CustomTextInput
              data-tetsid="telephone-input"
              name="telephone"
              control={control}
              errors={errors}
              readOnly={readOnly || isFieldDisabled('telephone')}
              disabled={isFormDisabled}
              label={labels.telephone ?? 'Telephone'}
            />
          </Grid>
        </Grid>

        <Typography variant="h6" sx={{ mb: '1.5rem', pt: '1rem' }}>
          {sections.business_identifiers ?? 'Business Identifiers'}
          <Divider></Divider>
        </Typography>

        <Grid container spacing={2}>
          {edit && !hasExistingVat && (
            <Grid size={{ xs: 12 }}>
              <Typography variant="body2" color="text.secondary">
                {formTexts.tax_not_on_file ??
                  'Tax details are not present for this profile. You can complete other details without VAT.'}
              </Typography>
            </Grid>
          )}
          <Grid size={{ xs: 12, sm: 6 }}>
            <TaxSchemeSelect
              data-testid="tax_scheme-select"
              name="party_tax_scheme.tax_scheme"
              control={control}
              errors={errors}
              readOnly={readOnly || isFieldDisabled('party_tax_scheme.tax_scheme') || edit}
              disabled={isFormDisabled || edit}
              label={labels.tax_scheme ?? 'Tax Scheme'}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <CustomTextInput
              data-testid="tax_identifier-input"
              name="party_tax_scheme.company_id"
              control={control}
              errors={errors}
              readOnly={readOnly || isFieldDisabled('party_tax_scheme.company_id') || edit}
              disabled={isFormDisabled || edit}
              label={labels.vat ?? 'VAT'}
              inputProps={{ maxLength: 15 }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <CustomTextInput
              data-testid="tin-input"
              name="tin"
              control={control}
              errors={errors}
              readOnly={readOnly || isFieldDisabled('tin') || edit}
              disabled={isFormDisabled}
              label={labels.tin ?? 'TIN'}
              inputProps={{ maxLength: 10 }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <RegistrationTypeSelect
              data-testid="rscheme_agency_id-select"
              errors={errors}
              readOnly={readOnly}
              disabled={isFormDisabled}
              control={control}
              name="scheme_agency_id"
              onChange={handleRegisterType}
              overrideOptions={b2bRegistrationTypeOptions}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <CustomTextInput
              data-testid="registration_number-input"
              name="company_id"
              control={control}
              errors={errors}
              readOnly={readOnly}
              disabled={isFormDisabled}
              label={registrationNumberLabel}
            />
          </Grid>
          {registrationType?.code === 'TL' && (
            <Grid size={{ xs: 12, sm: 6 }}>
              <CustomTextInput
                data-testid="registration_authority-input"
                name="authority_name"
                control={control}
                errors={errors}
                readOnly={readOnly}
                disabled={isFormDisabled}
                label={labels.registration_authority ?? 'Registration Authority'}
              />
            </Grid>
          )}
          {registrationType?.code === 'PAS' && (
            <Grid size={{ xs: 12, sm: 6 }}>
              <CountrySelect
                data-testid="passport_issuing_country_code-select"
                name="passport_issuing_country_code"
                control={control}
                errors={errors}
                readOnly={readOnly}
                disabled={isFormDisabled}
                label={labels.passport_issuing_country ?? 'Passport Issuing Country'}
              />
            </Grid>
          )}
          <Grid size={{ xs: 12, sm: 6 }}>
            <PartyIdentificationSchemeSelect
              data-testid="party_identification_scheme_id-select"
              name="party_identification_scheme_id"
              control={control}
              errors={errors}
              readOnly={readOnly}
              disabled={isFormDisabled}
              label={labels.party_identification_scheme_id ?? 'Party Identification Scheme'}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <CustomTextInput
              data-testid="party_identification_id-input"
              name="party_identification_id"
              control={control}
              errors={errors}
              readOnly={readOnly}
              disabled={isFormDisabled}
              label={labels.party_identification_id ?? 'Party Identification'}
            />
          </Grid>
        </Grid>

        {/* {edit && isPeppolEmpty && (
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 12 }}>
              {subscription?.features?.enable_phase2 && (
                <PeppolRegistrationSteps
                  profileId={initialData?.profile_id}
                  control={control}
                  errors={errors}
                  getValues={getValues}
                  setParticipantId={setParticipantId}
                  handleFetchParticipants={handleFetchParticipants}
                  loading={loading}
                  readOnly={readOnly}
                  loadingParticipant={loadingParticipant}
                  isPeppolEmpty={isPeppolEmpty}
                  peppolParticipant={peppolParticipant}
                />
              )}
            </Grid>
          </Grid>
        )} */}

        {/* {(readOnly || (edit && !isPeppolEmpty)) && ( */}
        {(readOnly || edit) && (
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 12 }}>
              <PeppolDetails
                control={control}
                errors={errors}
                getValues={getValues}
                setParticipantId={setParticipantId}
                handleFetchParticipants={handleFetchParticipants}
                loading={loading}
                readOnly={true}
                loadingParticipant={loadingParticipant}
                isPeppolEmpty={isPeppolEmpty}
                peppolParticipant={peppolParticipant}
                peppolLabels={labels}
              />
            </Grid>
          </Grid>
        )}
      </Box>
    </Paper>
  );
}
